/* eslint-disable react/prop-types */
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { cloneDeep, isEqual } from "lodash";
import { ChangeEvent, FC, useContext, useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { PPProps } from "./PPField";

type InnerCheckBoxFieldProps = {
  props: PPProps;
  field: ControllerRenderProps<FieldValues, string>;
};

const InnerCheckBoxField = ({ props, field }: InnerCheckBoxFieldProps) => {
  let fontColor: string = "text.secondary";
  if (props.extendedAttributes?.fontColor)
    fontColor = props.extendedAttributes.fontColor;
  const fieldIsRequired: string = "Field is required";
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];

  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (appContext: AppContextType) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext
        );
      });
    }
  };

  const [errorMessage, setErrorMessage]: [
    string,
    React.Dispatch<React.SetStateAction<string>>
  ] = useState(() => fieldIsRequired);

  const isFieldInvalid: boolean =
    appContext.fieldState[field.name] !== undefined &&
    !appContext.fieldState[field.name];
  const isHideField: boolean =
    appContext.fieldHide[field.name] !== undefined &&
    appContext.fieldHide[field.name];
  const isDisabledField: boolean =
    appContext.fieldDisable[field.name] !== undefined &&
    appContext.fieldDisable[field.name];

  return (
    <FormControl error={isFieldInvalid}>
      <FormControlLabel
        sx={isHideField ? { display: "none" } : { justifyContent: "left" }}
        control={
          <Checkbox
            value={field.value}
            checked={
              !!stateCtx.applicationContext.checkboxChecked.checked[field.name]
            }
            onChange={(_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
              field.onChange(checked ? "y" : "");

              const newAppCtx: AppContextType = cloneDeep(appContext);
              newAppCtx.checkboxChecked.checked[field.name] = checked;
              newAppCtx.fieldState[field.name] =
                !isRequired || (isRequired && checked);
              onChange(newAppCtx);
              if (!isEqual(stateCtx.applicationContext, newAppCtx))
                setAppContext(newAppCtx);
            }}
            id={props.id}
            onClick={() => {
              const newAppCtx: AppContextType = cloneDeep(appContext);
              newAppCtx.fieldState[field.name] =
                !isRequired ||
                (isRequired &&
                  newAppCtx.checkboxChecked.checked[field.name] !== undefined &&
                  newAppCtx.checkboxChecked.checked[field.name]);
              if (!isEqual(stateCtx.applicationContext, newAppCtx))
                setAppContext(newAppCtx);
            }}
          />
        }
        label={
          <FormLabel sx={{ color: fontColor }}>
            {props.label + (isRequired ? " *" : "")}
          </FormLabel>
        }
        labelPlacement="start"
      />
      <FormHelperText sx={isHideField ? { display: "none" } : null}>
        {isFieldInvalid ? (
          fieldIsRequired
        ) : (
          <HelperText helperText={props.helperText}></HelperText>
        )}
      </FormHelperText>
    </FormControl>
  );
};

const PPCheckbox: FC<PPProps> = (props) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={props.id}
      control={control}
      defaultValue={""}
      render={({ field }) => {
        return <InnerCheckBoxField props={props} field={field} />;
      }}
    />
  );
};

export default PPCheckbox;
//PPCheckbox.whyDidYouRender = true;
